/**
 * A wall-clock stopwatch that measures elapsed running time across start/pause/resume.
 *
 * Elapsed time is *computed* from the injected clock on every read rather than
 * accumulated tick-by-tick, so it is immune to two failure modes that bit the old
 * recording timer (IH-6926): a JS timer that the OS throttles/keeps-alive in the
 * background, and an event handler that fires more than once (e.g. a replayed
 * APP_FOREGROUNDED). Re-reading elapsedMs() is a pure computation and can never
 * double-count.
 *
 * The clock is injected so timing logic is unit-testable without real time.
 */
export class Stopwatch {
    private accumulatedMs = 0;
    private runStartedAt: number | null = null;

    constructor(private now: () => number = () => Date.now()) {}

    /** Begin timing from zero (resets any prior elapsed time). */
    start(): void {
        this.accumulatedMs = 0;
        this.runStartedAt = this.now();
    }

    /** Freeze elapsed time. Time passing while paused does not count. No-op if not running. */
    pause(): void {
        if (this.runStartedAt !== null) {
            this.accumulatedMs += this.now() - this.runStartedAt;
            this.runStartedAt = null;
        }
    }

    /** Continue timing from the frozen elapsed value. No-op if already running. */
    resume(): void {
        if (this.runStartedAt === null) {
            this.runStartedAt = this.now();
        }
    }

    /** Clear elapsed time and stop running. */
    reset(): void {
        this.accumulatedMs = 0;
        this.runStartedAt = null;
    }

    isRunning(): boolean {
        return this.runStartedAt !== null;
    }

    elapsedMs(): number {
        const currentRun = this.runStartedAt !== null ? this.now() - this.runStartedAt : 0;
        return this.accumulatedMs + currentRun;
    }
}
